/**
 * Domain types for mockExam feature (模試).
 * Aligned with backend DTOs from app-mock-exam.
 */
import { valueOf } from "@/types/common";

/** MockExam metadata */
export type TMockExamMetadata = {
  totalQuestions: number;
  timeLimitMinutes: number;
  passingScorePercentage: number;
  passingQuestionCount: number;
  /** Raw score points of a perfect result (満点) */
  totalScore: number;
  /** Raw score points required to pass (合格基準) */
  passingScore: number;
};

/** Achievement grade (到達度判定). Computed on the backend and returned as-is. */
export type TMockExamGrade = "A" | "B" | "C" | "D" | "E";

/** Single mockExam attempt result */
export type TMockExamAttempt = {
  attemptId: number;
  date: string;
  grade: TMockExamGrade;
  scorePercentage: number;
  correctAnswers: number;
  totalQuestions: number;
  timeSpentSeconds: number | null;
};

/** MockExam page data combining metadata and history for one exam */
export type TMockExamDetail = {
  mockExamId: number;
  name: string;
  metadata: TMockExamMetadata;
  attempts: TMockExamAttempt[];
};

// ─── MockExam List Types ───────────────────────────

/** A learner's most recent completed attempt, shown as「前回の試験」on the list */
export type TMockExamLastAttempt = {
  date: string;
  grade: TMockExamGrade;
  scorePercentage: number;
};

/** A single active mock exam shown in the list */
export type TMockExamListItem = {
  mockExamId: number;
  name: string;
  timeLimitMinutes: number;
  passingScorePercentage: number;
  lastAttempt: TMockExamLastAttempt | null;
};

// ─── MockExam Session (Quiz) Types ───────────────────────────

export type TMockExamSessionQuestionChoice = {
  choiceId: string;
  choiceText: string;
};
/** Single question within an mockExam session */
export type TMockExamSessionQuestion = {
  questionId: number;
  questionText: string;
  imageFile: string | null;
  choices: TMockExamSessionQuestionChoice[];
};

/** Subject group within an mockExam session */
export type TMockExamSessionSubject = {
  subjectId: string;
  subjectName: string;
  questions: TMockExamSessionQuestion[];
};

/** MockExam session returned after starting a new attempt */
export type TMockExamSession = {
  attemptId: number;
  finishedAt: string;
  subjects: TMockExamSessionSubject[];
};

// ─── MockExam Submit Types ───────────────────────────

/** Request to submit all mockExam answers at once */
export type TMockExamSubmitRequest = {
  attemptId: number;
  timeSpentSeconds: number;
  answers: {
    questionId: number;
    selectedAnswer: string | null;
  }[];
};

/** Server response after submitting mockExam */
export type TMockExamSubmitResult = {
  attemptId: number;
  earnedScore: number;
  totalScore: number;
  passingScore: number;
  isPassed: boolean;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  totalQuestions: number;
  timeSpentSeconds: number;
  scorePercentage: number;
  passingPercentage: number;
};

// ─── MockExam Result Types ───────────────────────────

/** Filter type for mockExam result questions */
export const MOCK_EXAM_RESULT_FILTER = {
  ALL: "all",
  CORRECT: "correct",
  INCORRECT: "incorrect",
  UNANSWERED: "unanswered",
} as const;

export type TMockExamResultFilterType = valueOf<typeof MOCK_EXAM_RESULT_FILTER>;

/** A single question in mockExam result */
export type TMockExamResultItem = {
  questionId: number;
  displayOrder: number;
  questionText: string;
  translatedQuestionText: string | null;
  imageFile: string | null;
  isCorrect: boolean;
  selectedOption: string | null;
  correctOption: string;
  explanation: string;
  translatedExplanation: string | null;
  options: string[];
  translatedOptions: (string | null)[] | null;
};

/** A subject group in mockExam result */
export type TMockExamResultSubject = {
  subjectId: string;
  subjectName: string;
  items: TMockExamResultItem[];
};

/** Full mockExam result data */
export type TMockExamResult = {
  attemptId: number;
  mockExamId: number;
  grade: TMockExamGrade;
  correctCount: number;
  totalQuestions: number;
  scorePercentage: number;
  /** Raw score points earned in this attempt */
  earnedScore: number;
  /** Raw score points of a perfect result (満点) */
  totalScore: number;
  timeSpentSeconds: number | null;
  subjects: TMockExamResultSubject[];
  finishedAt: string;
};

// ─── Backend Response DTOs ───────────────────────────

export type TMockExamDetailAttemptDto = {
  attemptId: number;
  startedAt: string;
  correctCount: number | null;
  totalQuestions: number;
  timeSpentSeconds: number | null;
  scorePercentage: number | null;
  grade: TMockExamGrade;
  status: string;
};

export type TListLastAttemptDto = {
  startedAt: string;
  scorePercentage: number;
  grade: TMockExamGrade;
};

export type TMockExamListItemDto = {
  mockExamId: number;
  name: string;
  examDuration: number | null;
  totalScore: number | null;
  passingScore: number | null;
  lastAttempt: TListLastAttemptDto | null;
};

export type TMockExamDetailDto = {
  mockExamId: number;
  name: string;
  industryId: number;
  industryName: string;
  examDuration: number | null;
  totalScore: number | null;
  passingScore: number | null;
  totalQuestions: number;
  subjects: { subjectName: string; questionCount: number }[];
  attempts: TMockExamDetailAttemptDto[];
};

export type TStartQuestionDto = {
  questionId: number;
  displayOrder: number;
  questionText: string;
  imageFile: string | null;
  choices: string[];
};

export type TStartSubjectDto = {
  subjectName: string;
  questions: TStartQuestionDto[];
};

export type TStartDto = {
  attemptId: number;
  examDuration: number;
  totalScore: number;
  passingScore: number;
  subjects: TStartSubjectDto[];
};

export type TSubmitDto = {
  attemptId: number;
  earnedScore: number;
  totalScore: number;
  passingScore: number;
  isPassed: boolean;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  totalQuestions: number;
  timeSpentSeconds: number;
  scorePercentage: number;
  passingPercentage: number;
};

export type TReviewQuestionDto = {
  questionId: number;
  displayOrder: number;
  questionText: string;
  translatedQuestionText: string | null;
  imageFile: string | null;
  correctAnswer: string;
  translatedCorrectAnswer: string | null;
  selectedAnswer: string | null;
  isCorrect: boolean | null;
  score: number;
  earnedScore: number;
  explanation: string | null;
  translatedExplanation: string | null;
  choices: string[];
  translatedChoices: (string | null)[] | null;
};

export type TReviewSubjectDto = {
  subjectName: string;
  questions: TReviewQuestionDto[];
};

export type TReviewDto = {
  attemptId: number;
  mockExamId: number;
  grade: TMockExamGrade;
  earnedScore: number;
  totalScore: number;
  timeSpentSeconds: number | null;
  subjects: TReviewSubjectDto[];
  finishedAt: string;
};
